import { FormEvent, useEffect, useMemo, useState } from 'react';
import { api, getToken, clearSession, urlImagem } from '../lib/api';
import { useAuth } from '../lib/auth';
import { PRIORIDADE, dataHora, rotuloPrioridade, rotuloStatus } from '../lib/rotulos';
import AssinaturaPad from '../components/AssinaturaPad';
import SeletorFotos from '../components/SeletorFotos';

interface Opcao { id: number; nome: string; }
interface Piso extends Opcao { unidade_id: number; }
interface Local extends Opcao { unidade_id: number; piso_id: number; }
interface Tecnico extends Opcao { unidades?: number[]; todas_unidades?: boolean; }
interface Categoria extends Opcao { tipo: string; }
interface Ativo extends Opcao { unidade_id: number; piso_id: number; local_id: number; qr_code?: string | null; categoria_id?: number | null; }
interface AtivoConsumo extends Opcao {
  patrimonio?: string | null;
  marca?: string | null;
  total_medidores: number;
  medidores_ativos: number;
  ultima_leitura?: string | null;
}
interface CampoConsumo {
  id: number;
  codigo: string;
  rotulo: string;
  unidade_medida?: string | null;
  obrigatorio: boolean;
  status: string;
}
interface MedidorConsumo extends Opcao {
  tipo_consumo: 'agua' | 'gas' | 'energia_eletrica';
  unidade_medida: string;
  exigir_imagem_leitura?: boolean;
  status: string;
  campos: CampoConsumo[];
  ultima_leitura?: {
    id: number;
    data_leitura: string;
    valores: Record<string, string>;
  } | null;
}
interface OSItem {
  id: number;
  codigo: string;
  status: string;
  tipo_os: string;
  prioridade: string;
  unidade_nome: string | null;
  piso_nome: string | null;
  local_nome: string | null;
  ativo_nome: string | null;
  tecnico_nome: string | null;
  data_agendada: string | null;
  hora_agendada: string | null;
  created_at: string;
}
interface ChecklistResp {
  id: number;
  checklist_item_id: number;
  descricao: string;
  marcado: boolean | string;
  observacao: string | null;
  imagem_url: string | null;
}
interface ChecklistItem {
  id: number;
  descricao: string;
  obrigatorio: boolean | string;
  exige_foto: boolean | string;
  exige_observacao: boolean | string;
  ordem: number;
}
interface ChecklistModelo { id: number; nome: string; itens: ChecklistItem[]; }
interface HistoricoOrcamento {
  id: number;
  usuario_id: number | null;
  usuario_nome?: string | null;
  valor_anterior: string | number | null;
  valor_novo: string | number | null;
  created_at: string;
}
interface OSDetalheData extends OSItem {
  ativo_id: number | null;
  ativo_categoria_id: number | null;
  solicitante_nome: string | null;
  avaria: string | null;
  descricao: string | null;
  observacao: string | null;
  tempo_total_minutos: number | null;
  orcamento_valor?: string | number | null;
  orcamento_historico?: HistoricoOrcamento[];
  assinatura_tecnico_url: string | null;
  assinatura_cliente_url: string | null;
  checklist: ChecklistResp[];
}

const STATUS = ['aberto', 'em_andamento', 'interrompido', 'aguardando_aprovacao', 'concluido', 'cancelado'];
type AbaMobile = 'os' | 'agenda' | 'nova' | 'consumo' | 'ajustes';

function bool(v: unknown): boolean {
  return v === true || v === 'true' || v === '1' || v === 1;
}

function podeMudarEstado(atual: string, destino: string): boolean {
  if (atual === destino) return false;
  if (['concluido', 'cancelado'].includes(atual)) return false;
  if (destino === 'cancelado') return true;
  if (atual === 'aberto') return destino === 'em_andamento';
  if (atual === 'em_andamento') return ['interrompido', 'aguardando_aprovacao'].includes(destino);
  if (atual === 'interrompido') return destino === 'em_andamento';
  if (atual === 'aguardando_aprovacao') return destino === 'concluido';
  return false;
}

function rotuloBotao(atual: string, destino: string): string {
  if (atual === 'aberto' && destino === 'em_andamento') return 'Iniciar';
  if (atual === 'interrompido' && destino === 'em_andamento') return 'Retomar';
  return rotuloStatus(destino);
}

function localOs(os: Pick<OSItem, 'unidade_nome' | 'piso_nome' | 'local_nome' | 'ativo_nome'>): string {
  return [os.unidade_nome, os.piso_nome, os.local_nome].filter(Boolean).join(' / ') || os.ativo_nome || '-';
}

function moeda(v: unknown): string {
  const n = Number(v);
  const seguro = Number.isFinite(n) ? n : 0;
  return 'R$ ' + seguro.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function moedaMascara(centavos: string): string {
  if (!centavos) return '';
  return moeda(Number(centavos) / 100);
}

const hoje = () => new Date().toISOString().slice(0, 10);

function numero(v?: string | number | null): string {
  if (v === null || v === undefined || v === '') return '-';
  const n = typeof v === 'number' ? v : Number(v);
  if (Number.isNaN(n)) return String(v);
  return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 4 }).format(n);
}

function dataBr(data?: string | null): string {
  if (!data) return '-';
  const partes = String(data).split('-');
  return partes.length === 3 ? `${partes[2]}/${partes[1]}/${partes[0]}` : String(data);
}

function tipoConsumoLabel(tipo: string): string {
  if (tipo === 'agua') return 'Água';
  if (tipo === 'gas') return 'Gás';
  if (tipo === 'energia_eletrica') return 'Energia elétrica';
  return tipo;
}

export default function MobileApp() {
  const { user, login, logout, isOrcamentoOsDisponivel, isPremium, isConsumoDisponivel } = useAuth();
  const podeVerValoresMonetarios = !user?.somente_gestor_supervisor_pode_ver_valores_habilitado
    || ['admin_geral', 'admin_empresa', 'supervisor'].includes(user?.perfil || '');
  const podeUsarOrcamentoOs = isOrcamentoOsDisponivel && podeVerValoresMonetarios;
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [erro, setErro] = useState('');
  const [msg, setMsg] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [aba, setAba] = useState<AbaMobile>('os');
  const [lista, setLista] = useState<OSItem[]>([]);
  const [statusFiltro, setStatusFiltro] = useState('');
  const [detalheId, setDetalheId] = useState<number | null>(null);

  async function entrar(e: FormEvent) {
    e.preventDefault();
    setErro('');
    setCarregando(true);
    try {
      await login(email.trim(), senha);
    } catch (err) {
      setErro(err instanceof Error ? err.message : 'Falha no login');
    } finally {
      setCarregando(false);
    }
  }

  async function sair() {
    await logout();
    clearSession();
  }

  async function carregarLista() {
    setErro('');
    const qs = statusFiltro ? '?status=' + encodeURIComponent(statusFiltro) : '';
    try {
      setLista(await api<OSItem[]>('/os/os_listar.php' + qs));
    } catch (err) {
      setErro(err instanceof Error ? err.message : String(err));
    }
  }

  useEffect(() => {
    if (getToken()) carregarLista();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFiltro, user?.id]);

  const agenda = useMemo(() => (
    lista
      .filter((os) => os.data_agendada && !['concluido', 'cancelado'].includes(os.status))
      .sort((a, b) => String(a.data_agendada).localeCompare(String(b.data_agendada)) || String(a.hora_agendada || '').localeCompare(String(b.hora_agendada || '')))
  ), [lista]);
  const mostrarAjustesMobile = Boolean(user?.rastreamento_tecnico_habilitado && user?.perfil === 'tecnico');
  const podeCriarOs = !(user?.perfil === 'tecnico' && user?.tecnico_nao_pode_criar_os);
  const navItems = [
    { id: 'os' as AbaMobile, label: 'OS', show: true },
    { id: 'agenda' as AbaMobile, label: 'Agenda', show: true },
    { id: 'nova' as AbaMobile, label: 'Nova OS', show: podeCriarOs },
    { id: 'consumo' as AbaMobile, label: 'Consumo', show: isConsumoDisponivel },
    { id: 'ajustes' as AbaMobile, label: 'Ajustes', show: mostrarAjustesMobile },
  ].filter((item) => item.show);

  if (!getToken()) {
    return (
      <div className="mobile-app login">
        <div className="mobile-login-card">
          <img src="./logo_manux.jpeg" alt="Manux Service" />
          <h1>App de campo</h1>
          <p>Entre para executar OS, checklist, agenda e assinaturas.</p>
          {erro && <div className="msg-erro">{erro}</div>}
          <form onSubmit={entrar}>
            <label>E-mail</label>
            <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            <label>Senha</label>
            <input className="input" type="password" value={senha} onChange={(e) => setSenha(e.target.value)} required />
            <button className="btn lg" disabled={carregando}>{carregando ? 'Entrando...' : 'Entrar'}</button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="mobile-app">
      <header className="mobile-top">
        <div>
          <strong>Manux Service</strong>
          <span>{user?.nome || 'Equipe de campo'}</span>
        </div>
        <button className="btn sec sm" onClick={sair}>Sair</button>
      </header>

      {erro && <div className="mobile-alert msg-erro">{erro}</div>}
      {msg && <div className="mobile-alert msg-ok">{msg}</div>}

      {detalheId ? (
        <MobileOSDetalhe
          id={detalheId}
          isPremium={isPremium}
          podeOrcamento={podeUsarOrcamentoOs}
          voltar={() => { setDetalheId(null); carregarLista(); }}
          setErro={setErro}
          setMsg={setMsg}
        />
      ) : (
        <>
          {aba === 'os' && (
            <section className="mobile-screen">
              <div className="mobile-section-head">
                <h2>Ordens de serviço</h2>
                <button className="btn sec sm" onClick={carregarLista}>Atualizar</button>
              </div>
              <div className="mobile-status-tabs">
                <button className={!statusFiltro ? 'on' : ''} onClick={() => setStatusFiltro('')}>Todas</button>
                {STATUS.map((s) => <button key={s} className={statusFiltro === s ? 'on' : ''} onClick={() => setStatusFiltro(s)}>{rotuloStatus(s)}</button>)}
              </div>
              <div className="mobile-os-list">
                {lista.map((os) => <MobileOSCard key={os.id} os={os} abrir={() => setDetalheId(os.id)} />)}
                {!lista.length && <p className="vazio">Nenhuma OS encontrada.</p>}
              </div>
            </section>
          )}

          {aba === 'agenda' && (
            <section className="mobile-screen">
              <div className="mobile-section-head"><h2>Agenda do técnico</h2><button className="btn sec sm" onClick={carregarLista}>Atualizar</button></div>
              <div className="mobile-os-list">
                {agenda.map((os) => <MobileOSCard key={os.id} os={os} abrir={() => setDetalheId(os.id)} agenda />)}
                {!agenda.length && <p className="vazio">Nenhuma OS agendada em aberto.</p>}
              </div>
            </section>
          )}

          {aba === 'nova' && <MobileNovaOS onCriada={(id) => { setDetalheId(id); carregarLista(); }} setErro={setErro} setMsg={setMsg} podeOrcamento={podeUsarOrcamentoOs} />}
          {aba === 'consumo' && isConsumoDisponivel && <MobileConsumo setErro={setErro} setMsg={setMsg} />}
          {aba === 'ajustes' && mostrarAjustesMobile && <MobileAjustes setMsg={setMsg} />}
        </>
      )}

      {!detalheId && (
        <nav className="mobile-nav" style={{ gridTemplateColumns: `repeat(${navItems.length},1fr)` }}>
          {navItems.map((item) => (
            <button key={item.id} className={aba === item.id ? 'on' : ''} onClick={() => setAba(item.id)}>
              {item.label}
            </button>
          ))}
        </nav>
      )}
    </div>
  );
}

function MobileConsumo({ setErro, setMsg }: { setErro: (s: string) => void; setMsg: (s: string) => void }) {
  const [ativos, setAtivos] = useState<AtivoConsumo[]>([]);
  const [medidores, setMedidores] = useState<MedidorConsumo[]>([]);
  const [ativoId, setAtivoId] = useState('');
  const [medidorId, setMedidorId] = useState('');
  const [dataLeitura, setDataLeitura] = useState(hoje());
  const [valores, setValores] = useState<Record<string, string>>({});
  const [observacao, setObservacao] = useState('');
  const [fotos, setFotos] = useState<File[]>([]);
  const [salvando, setSalvando] = useState(false);
  const [carregando, setCarregando] = useState(false);

  const medidorAtual = useMemo(
    () => medidores.find((medidor) => String(medidor.id) === medidorId) || null,
    [medidores, medidorId],
  );
  const camposAtivos = useMemo(
    () => medidorAtual?.campos.filter((campo) => campo.status === 'ativo') || [],
    [medidorAtual],
  );

  async function carregarAtivos() {
    setCarregando(true);
    setErro('');
    try {
      const lista = await api<AtivoConsumo[]>('/consumo/ativos.php');
      setAtivos(lista);
      setAtivoId((atual) => (atual && lista.some((ativo) => String(ativo.id) === atual) ? atual : (lista[0] ? String(lista[0].id) : '')));
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
    } finally {
      setCarregando(false);
    }
  }

  async function carregarMedidores(ativo: string) {
    if (!ativo) {
      setMedidores([]);
      setMedidorId('');
      return;
    }
    setErro('');
    try {
      const lista = await api<MedidorConsumo[]>(`/consumo/medidores.php?ativo_id=${ativo}`);
      const ativosDoEquipamento = lista.filter((medidor) => medidor.status === 'ativo');
      setMedidores(ativosDoEquipamento);
      setMedidorId((atual) => (atual && ativosDoEquipamento.some((medidor) => String(medidor.id) === atual) ? atual : (ativosDoEquipamento[0] ? String(ativosDoEquipamento[0].id) : '')));
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
    }
  }

  useEffect(() => {
    carregarAtivos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    carregarMedidores(ativoId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ativoId]);

  useEffect(() => {
    if (!medidorAtual) {
      setValores({});
      return;
    }
    setValores(Object.fromEntries(camposAtivos.map((campo) => [campo.codigo, ''])));
    setFotos([]);
    setObservacao('');
  }, [medidorAtual, camposAtivos]);

  async function enviarFoto(leituraId: number) {
    if (!fotos.length) return;
    const fd = new FormData();
    fd.append('leitura_id', String(leituraId));
    fd.append('arquivo', fotos[0]);
    await api('/consumo/midias.php', { method: 'POST', body: fd, isForm: true });
  }

  async function registrar(e: FormEvent) {
    e.preventDefault();
    if (!medidorAtual) {
      setErro('Selecione um medidor.');
      return;
    }
    if (medidorAtual.exigir_imagem_leitura !== false && !fotos.length) {
      setErro('Anexe a foto da leitura.');
      return;
    }
    setErro('');
    setMsg('');
    setSalvando(true);
    try {
      const resp = await api<{ id: number }>('/consumo/leituras.php', {
        method: 'POST',
        body: {
          medidor_id: medidorAtual.id,
          data_leitura: dataLeitura,
          observacao: observacao || null,
          valores,
        },
      });
      await enviarFoto(resp.id);
      setMsg('Consumo registrado.');
      setDataLeitura(hoje());
      setValores(Object.fromEntries(camposAtivos.map((campo) => [campo.codigo, ''])));
      setFotos([]);
      setObservacao('');
      await carregarMedidores(ativoId);
    } catch (err) {
      setErro(err instanceof Error ? err.message : String(err));
    } finally {
      setSalvando(false);
    }
  }

  return (
    <section className="mobile-screen">
      <div className="mobile-section-head">
        <h2>Registro de consumo</h2>
        <button className="btn sec sm" onClick={carregarAtivos}>{carregando ? 'Carregando...' : 'Atualizar'}</button>
      </div>
      {!ativos.length && !carregando && <p className="vazio">Nenhum equipamento com medidor de consumo.</p>}
      {!!ativos.length && (
        <form className="mobile-form" onSubmit={registrar}>
          <label>Equipamento</label>
          <select value={ativoId} onChange={(e) => setAtivoId(e.target.value)} required>
            {ativos.map((ativo) => (
              <option key={ativo.id} value={ativo.id}>
                {ativo.nome}{ativo.patrimonio ? ` - ${ativo.patrimonio}` : ''}
              </option>
            ))}
          </select>
          <label>Medidor</label>
          <select value={medidorId} onChange={(e) => setMedidorId(e.target.value)} required disabled={!medidores.length}>
            {medidores.map((medidor) => (
              <option key={medidor.id} value={medidor.id}>
                {medidor.nome} - {tipoConsumoLabel(medidor.tipo_consumo)}
              </option>
            ))}
            {!medidores.length && <option value="">Sem medidor ativo</option>}
          </select>
          {medidorAtual?.ultima_leitura && (
            <div className="mobile-info">
              <strong>Última leitura</strong>
              <span>Data: {dataBr(medidorAtual.ultima_leitura.data_leitura)}</span>
              {Object.entries(medidorAtual.ultima_leitura.valores || {}).map(([codigo, valor]) => (
                <span key={codigo}>{codigo}: {numero(valor)}</span>
              ))}
            </div>
          )}
          <label>Data da leitura</label>
          <input type="date" value={dataLeitura} onChange={(e) => setDataLeitura(e.target.value)} required />
          {camposAtivos.map((campo) => (
            <div key={campo.id}>
              <label>{campo.rotulo}</label>
              <input
                value={valores[campo.codigo] || ''}
                onChange={(e) => setValores((atual) => ({ ...atual, [campo.codigo]: e.target.value }))}
                placeholder={campo.unidade_medida || medidorAtual?.unidade_medida || ''}
                inputMode="decimal"
                required={campo.obrigatorio}
              />
            </div>
          ))}
          <label>Foto da leitura {medidorAtual?.exigir_imagem_leitura !== false ? '*' : '(opcional)'}</label>
          <SeletorFotos arquivos={fotos} onChange={setFotos} max={1} />
          <label>Observação</label>
          <textarea value={observacao} onChange={(e) => setObservacao(e.target.value)} rows={3} />
          <button className="btn lg" disabled={salvando || !medidorAtual}>{salvando ? 'Registrando...' : 'Registrar consumo'}</button>
        </form>
      )}
    </section>
  );
}

function MobileAjustes({ setMsg }: { setMsg: (s: string) => void }) {
  const android = /Android/i.test(navigator.userAgent);

  function abrirPermissaoSobreposicao() {
    setMsg('Se a tela não abrir, acesse Configurações > Apps > Manux Service > Sobrepor a outros apps e permita a sobreposição.');
    if (!android) return;
    window.location.href = 'intent://settings#Intent;action=android.settings.MANAGE_OVERLAY_PERMISSION;end';
  }

  return (
    <section className="mobile-screen">
      <h2>Ajustes do técnico</h2>
      <div className="mobile-panel">
        <h3>Rastreabilidade perfeita</h3>
        <p style={{ color: 'var(--texto-suave)', marginTop: 0 }}>
          Permita que o app sobreponha outros apps para manter os avisos e a rastreabilidade ativa durante o atendimento.
        </p>
        <button type="button" className="btn lg" onClick={abrirPermissaoSobreposicao}>
          Permitir sobrepor a outros apps
        </button>
        {!android && (
          <small style={{ color: 'var(--texto-suave)', display: 'block', marginTop: 10 }}>
            Essa permissão é específica do Android.
          </small>
        )}
      </div>
    </section>
  );
}

function MobileOSCard({ os, abrir, agenda = false }: { os: OSItem; abrir: () => void; agenda?: boolean }) {
  return (
    <button className="mobile-os-card" onClick={abrir}>
      <span className={'badge st-' + os.status}>{rotuloStatus(os.status)}</span>
      <strong>{os.codigo}</strong>
      <small>{os.tipo_os} - {rotuloPrioridade(os.prioridade)}</small>
      <span>{localOs(os)}</span>
      <em>{agenda ? dataHora([os.data_agendada, os.hora_agendada].filter(Boolean).join(' ')) : os.tecnico_nome || 'Sem técnico'}</em>
    </button>
  );
}

function MobileNovaOS({ onCriada, setErro, setMsg, podeOrcamento }: { onCriada: (id: number) => void; setErro: (s: string) => void; setMsg: (s: string) => void; podeOrcamento: boolean }) {
  const { isPremium, user } = useAuth();
  const [unidades, setUnidades] = useState<Opcao[]>([]);
  const [pisos, setPisos] = useState<Piso[]>([]);
  const [locais, setLocais] = useState<Local[]>([]);
  const [tecnicos, setTecnicos] = useState<Tecnico[]>([]);
  const [tipos, setTipos] = useState<Categoria[]>([]);
  const [ativos, setAtivos] = useState<Ativo[]>([]);
  const [form, setForm] = useState({
    unidade_id: '', piso_id: '', local_id: '', ativo_id: '', tecnico_id: '', tipo_os: '', prioridade: 'media',
    avaria: '', descricao: '', orcamento_valor: '', data_agendada: '', hora_agendada: '',
  });
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    Promise.all([
      api<Opcao[]>('/cadastros/unidades.php?somente_ativos=1'),
      api<Tecnico[]>('/cadastros/tecnicos.php'),
      api<Categoria[]>('/cadastros/categorias.php?somente_ativos=1'),
    ]).then(([u, t, c]) => {
      setUnidades(u);
      setTecnicos(t);
      setTipos(c);
      setForm((f) => ({ ...f, tipo_os: f.tipo_os || c[0]?.nome || 'Corretiva' }));
    }).catch((e) => setErro(e instanceof Error ? e.message : String(e)));
  }, [setErro]);

  useEffect(() => {
    if (isPremium) api<Ativo[]>('/ativos/ativos.php').then(setAtivos).catch(() => {});
  }, [isPremium]);

  useEffect(() => {
    setPisos([]);
    setLocais([]);
    setForm((f) => ({ ...f, piso_id: '', local_id: '' }));
    if (form.unidade_id) api<Piso[]>('/cadastros/pisos.php?somente_ativos=1&unidade_id=' + form.unidade_id).then(setPisos).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.unidade_id]);

  useEffect(() => {
    setLocais([]);
    setForm((f) => ({ ...f, local_id: '' }));
    if (form.piso_id) api<Local[]>('/cadastros/locais.php?somente_ativos=1&piso_id=' + form.piso_id).then(setLocais).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.piso_id]);

  const set = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }));
  const ativoSelecionado = ativos.find((a) => String(a.id) === form.ativo_id);
  const unidadeResponsavelId = form.unidade_id || (ativoSelecionado ? String(ativoSelecionado.unidade_id) : '');
  const tecnicosDaUnidade = tecnicos.filter((t) => {
    if (!user?.pode_trazer_tecnico_associado_unidade) {
      return !form.unidade_id || !t.unidades?.length || t.unidades.includes(Number(form.unidade_id));
    }
    if (!unidadeResponsavelId) return true;
    return Boolean(t.todas_unidades) || Boolean(t.unidades?.includes(Number(unidadeResponsavelId)));
  });
  const ativosDoLocal = ativos.filter((a) => !form.local_id || a.local_id === Number(form.local_id));

  async function salvar(e: FormEvent) {
    e.preventDefault();
    setErro('');
    setMsg('');
    setSalvando(true);
    try {
      const criado = await api<{ id: number }>('/os/os_criar.php', {
        method: 'POST',
        body: {
          ...(form.unidade_id ? { unidade_id: Number(form.unidade_id) } : {}),
          ...(form.piso_id ? { piso_id: Number(form.piso_id) } : {}),
          ...(form.local_id ? { local_id: Number(form.local_id) } : {}),
          ...(form.ativo_id ? { ativo_id: Number(form.ativo_id) } : {}),
          ...(form.tecnico_id ? { tecnico_id: Number(form.tecnico_id) } : {}),
          tipo_os: form.tipo_os,
          prioridade: form.prioridade,
          avaria: form.avaria || null,
          descricao: form.descricao || null,
          ...(podeOrcamento ? { orcamento_valor: moedaMascara(form.orcamento_valor) || null } : {}),
          data_agendada: form.data_agendada || null,
          hora_agendada: form.hora_agendada || null,
        },
      });
      setMsg('OS criada.');
      onCriada(criado.id);
    } catch (err) {
      setErro(err instanceof Error ? err.message : String(err));
    } finally {
      setSalvando(false);
    }
  }

  return (
    <section className="mobile-screen">
      <h2>Nova OS</h2>
      <form className="mobile-form" onSubmit={salvar}>
        <label>Tipo de chamado</label>
        <select value={form.tipo_os} onChange={(e) => set('tipo_os', e.target.value)} required>
          {tipos.map((t) => <option key={t.id} value={t.nome}>{t.nome}</option>)}
          {!tipos.length && <option value="Corretiva">Corretiva</option>}
        </select>
        <label>Prioridade</label>
        <select value={form.prioridade} onChange={(e) => set('prioridade', e.target.value)}>
          {Object.entries(PRIORIDADE).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <label>Unidade</label>
        <select value={form.unidade_id} onChange={(e) => set('unidade_id', e.target.value)}>
          <option value="">Selecione</option>
          {unidades.map((u) => <option key={u.id} value={u.id}>{u.nome}</option>)}
        </select>
        <label>Piso</label>
        <select value={form.piso_id} onChange={(e) => set('piso_id', e.target.value)} disabled={!form.unidade_id}>
          <option value="">Selecione</option>
          {pisos.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
        </select>
        <label>Local</label>
        <select value={form.local_id} onChange={(e) => set('local_id', e.target.value)} disabled={!form.piso_id}>
          <option value="">Selecione</option>
          {locais.map((l) => <option key={l.id} value={l.id}>{l.nome}</option>)}
        </select>
        {isPremium && (
          <>
            <label>Equipamento</label>
            <select value={form.ativo_id} onChange={(e) => set('ativo_id', e.target.value)}>
              <option value="">Sem equipamento</option>
              {ativosDoLocal.map((a) => <option key={a.id} value={a.id}>{a.nome}</option>)}
            </select>
          </>
        )}
        <label>Atribuir técnico</label>
        <select value={form.tecnico_id} onChange={(e) => set('tecnico_id', e.target.value)}>
          <option value="">Sem atribuição</option>
          {tecnicosDaUnidade.map((t) => <option key={t.id} value={t.id}>{t.nome}</option>)}
        </select>
        <label>Data agendada</label>
        <input type="date" value={form.data_agendada} onChange={(e) => set('data_agendada', e.target.value)} />
        <label>Hora agendada</label>
        <input type="time" value={form.hora_agendada} onChange={(e) => set('hora_agendada', e.target.value)} />
        <label>Avaria</label>
        <input value={form.avaria} onChange={(e) => set('avaria', e.target.value)} placeholder="Resumo curto" />
        <label>Descrição</label>
        <textarea value={form.descricao} onChange={(e) => set('descricao', e.target.value)} rows={4} placeholder="Detalhes do chamado" />
        {podeOrcamento && (
          <>
            <label>Orcamento</label>
            <input value={moedaMascara(form.orcamento_valor)} onChange={(e) => set('orcamento_valor', e.target.value.replace(/\D/g, ''))} placeholder="R$ 0,00" inputMode="numeric" />
          </>
        )}
        <button className="btn lg" disabled={salvando}>{salvando ? 'Criando...' : 'Criar OS'}</button>
      </form>
    </section>
  );
}

function MobileOSDetalhe({ id, isPremium, podeOrcamento, voltar, setErro, setMsg }: {
  id: number;
  isPremium: boolean;
  podeOrcamento: boolean;
  voltar: () => void;
  setErro: (s: string) => void;
  setMsg: (s: string) => void;
}) {
  const { user } = useAuth();
  const [os, setOs] = useState<OSDetalheData | null>(null);
  const [modelos, setModelos] = useState<ChecklistModelo[]>([]);
  const [respostas, setRespostas] = useState<Record<number, { marcado: boolean; observacao: string; imagem_url: string }>>({});
  const [salvandoChecklist, setSalvandoChecklist] = useState(false);
  const [orcamentoForm, setOrcamentoForm] = useState('');

  async function carregar() {
    const data = await api<OSDetalheData>('/os/os_detalhe.php?id=' + id);
    setOs(data);
    const mapa: Record<number, { marcado: boolean; observacao: string; imagem_url: string }> = {};
    (data.checklist || []).forEach((r) => {
      mapa[r.checklist_item_id] = { marcado: bool(r.marcado), observacao: r.observacao || '', imagem_url: r.imagem_url || '' };
    });
    setRespostas(mapa);
    if (isPremium && data.ativo_categoria_id) {
      setModelos(await api<ChecklistModelo[]>('/checklist/por_categoria.php?categoria_id=' + data.ativo_categoria_id));
    } else {
      setModelos([]);
    }
  }

  useEffect(() => {
    carregar().catch((e) => setErro(e instanceof Error ? e.message : String(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  useEffect(() => {
    const atual = os?.orcamento_valor;
    if (atual === null || atual === undefined || atual === '') {
      setOrcamentoForm('');
      return;
    }
    setOrcamentoForm(String(Math.round(Number(atual) * 100)));
  }, [os]);

  function setResposta(itemId: number, patch: Partial<{ marcado: boolean; observacao: string; imagem_url: string }>) {
    setRespostas((atuais) => ({
      ...atuais,
      [itemId]: { ...(atuais[itemId] || { marcado: false, observacao: '', imagem_url: '' }), ...patch },
    }));
  }

  async function mudarStatus(status: string) {
    if (!os) return;
    setErro('');
    setMsg('');
    try {
      await api('/os/os_atualizar_status.php', { method: 'POST', body: { id: os.id, status } });
      await carregar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
    }
  }
  async function salvarOrcamento() {
    if (!os) return;
    setErro('');
    setMsg('');
    try {
      await api('/os/os_orcamento.php', {
        method: 'POST',
        body: { ordem_servico_id: os.id, orcamento_valor: moedaMascara(orcamentoForm) || null },
      });
      setMsg('Orçamento salvo.');
      await carregar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
    }
  }

  async function salvarChecklist(e: FormEvent) {
    e.preventDefault();
    if (!os) return;
    const itens = modelos.flatMap((m) => m.itens);
    setSalvandoChecklist(true);
    setErro('');
    try {
      for (const item of itens) {
        const resp = respostas[item.id];
        if (bool(item.obrigatorio) && !resp?.marcado) throw new Error('Marque o item obrigatório: ' + item.descricao);
        if (bool(item.exige_observacao) && !(resp?.observacao || '').trim()) throw new Error('Preencha a observação: ' + item.descricao);
      }
      await api('/checklist/respostas.php', {
        method: 'POST',
        body: {
          ordem_servico_id: os.id,
          respostas: itens.map((item) => ({
            checklist_item_id: item.id,
            marcado: Boolean(respostas[item.id]?.marcado),
            observacao: respostas[item.id]?.observacao || null,
            imagem_url: respostas[item.id]?.imagem_url || null,
          })),
        },
      });
      setMsg('Checklist salvo.');
      await carregar();
    } catch (err) {
      setErro(err instanceof Error ? err.message : String(err));
    } finally {
      setSalvandoChecklist(false);
    }
  }

  async function salvarAssinatura(tipo: 'tecnico' | 'cliente', arquivo: File) {
    if (!os) return;
    const fd = new FormData();
    fd.append('ordem_servico_id', String(os.id));
    fd.append('tipo', tipo);
    fd.append('assinatura', arquivo);
    try {
      await api('/os/os_assinatura.php', { method: 'POST', body: fd, isForm: true });
      setMsg('Assinatura salva.');
      await carregar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
    }
  }

  if (!os) return <section className="mobile-screen"><p className="vazio">Carregando OS...</p></section>;
  // Mesma regra do OSDetalhe e do backend: a permissao de empresa
  // "LANCAR DADOS EM ORDENS DE SERVICO JA CONCLUIDA" destrava a OS CONCLUIDA
  // para gestor/supervisor. Cancelada continua travada para todos.
  const podeLancarEmOsConcluida = Boolean(user?.lancar_dados_os_concluida_habilitado)
    && ['admin_geral', 'admin_empresa', 'supervisor'].includes(user?.perfil || '');
  const encerrada = os.status === 'cancelado'
    || (os.status === 'concluido' && !podeLancarEmOsConcluida);

  return (
    <section className="mobile-screen mobile-detail">
      <button className="btn sec sm" onClick={voltar}>Voltar</button>
      <div className="mobile-detail-title">
        <span className={'badge st-' + os.status}>{rotuloStatus(os.status)}</span>
        <h2>{os.codigo}</h2>
        <p>{os.tipo_os} - {rotuloPrioridade(os.prioridade)}</p>
      </div>
      <div className="mobile-info">
        <strong>{localOs(os)}</strong>
        <span>Técnico: {os.tecnico_nome || '-'}</span>
        <span>Solicitante: {os.solicitante_nome || '-'}</span>
        {podeOrcamento && <span>Orçamento: {os.orcamento_valor === null || os.orcamento_valor === undefined || os.orcamento_valor === '' ? '-' : moeda(os.orcamento_valor)}</span>}
        {os.avaria && <span>Avaria: {os.avaria}</span>}
        {os.descricao && <span>Descrição: {os.descricao}</span>}
      </div>

      {podeOrcamento && !encerrada && (
        <div className="mobile-panel">
          <h3>Orçamento</h3>
          <label>Valor</label>
          <input value={moedaMascara(orcamentoForm)} onChange={(e) => setOrcamentoForm(e.target.value.replace(/\D/g, ''))} placeholder="R$ 0,00" inputMode="numeric" />
          <button type="button" className="btn sm" onClick={salvarOrcamento}>Salvar orçamento</button>
        </div>
      )}

      <h3>Estado da OS</h3>
      <div className="estado-os-grid">
        {STATUS.map((estado) => {
          const atual = os.status === estado;
          const habilitado = podeMudarEstado(os.status, estado);
          return (
            <button key={estado} type="button" className={'estado-os-btn st-' + estado + (atual ? ' atual' : '')} disabled={!habilitado} onClick={() => mudarStatus(estado)}>
              {rotuloBotao(os.status, estado)}
            </button>
          );
        })}
      </div>

      {isPremium && (
        <div className="mobile-panel">
          <h3>Checklist</h3>
          {!os.ativo_categoria_id && <p className="vazio">OS sem equipamento/tipo de chamado vinculado para checklist.</p>}
          {!!os.ativo_categoria_id && !modelos.length && <p className="vazio">Nenhum checklist cadastrado para este tipo de chamado.</p>}
          {!!modelos.length && (
            <form onSubmit={salvarChecklist}>
              {modelos.map((modelo) => (
                <div key={modelo.id} className="mobile-check-model">
                  <strong>{modelo.nome}</strong>
                  {modelo.itens.map((item) => (
                    <div key={item.id} className="mobile-check-item">
                      <label className="check-line">
                        <input type="checkbox" checked={Boolean(respostas[item.id]?.marcado)} onChange={(e) => setResposta(item.id, { marcado: e.target.checked })} disabled={encerrada} />
                        <span>{item.descricao}{bool(item.obrigatorio) ? ' *' : ''}</span>
                      </label>
                      <textarea value={respostas[item.id]?.observacao || ''} onChange={(e) => setResposta(item.id, { observacao: e.target.value })} placeholder="Observação" disabled={encerrada} />
                    </div>
                  ))}
                </div>
              ))}
              {!encerrada && <button className="btn" disabled={salvandoChecklist}>{salvandoChecklist ? 'Salvando...' : 'Salvar checklist'}</button>}
            </form>
          )}
        </div>
      )}

      <div className="mobile-panel">
        <h3>Assinaturas</h3>
        <AssinaturaPad titulo="Assinatura do técnico" imagemUrl={urlImagem(os.assinatura_tecnico_url)} bloqueado={encerrada} onSalvar={(arquivo) => salvarAssinatura('tecnico', arquivo)} />
        <AssinaturaPad titulo="Assinatura do cliente" imagemUrl={urlImagem(os.assinatura_cliente_url)} bloqueado={encerrada} onSalvar={(arquivo) => salvarAssinatura('cliente', arquivo)} />
      </div>
    </section>
  );
}
